import { html, LitElement } from 'lit'
import NavNode from './nav-node.js'
import './left-column.js'
import './right-column.js'
import './mobile-header.js'
import './scroll-indicator.js'

import styleUrl from '../../css/styles.css?url'

window.customElements.define(
    'hedelius-menu',
    class HedeliusMenu extends LitElement {
        static properties = {
            _rootNode: { type: Object },
            activeNode: { type: Object },
            _isMobile: { type: Boolean },
        }

        constructor() {
            super()
            this._rootNode = null
            this.activeNode = null
            this._isMobile = false
            this._menuOpenHandlers = []
            this._menuCloseHandlers = []
        }

        connectedCallback() {
            super.connectedCallback()
            this.#processUl()
            this.addEventListener('hedelius-column-click', this.#handleColumnClick)
        }

        /**
         * Populate the columns and add event listeners for menu buttons and media changes.
         */
        firstUpdated() {
            this._isMobile = window.matchMedia('(max-width: 768px)').matches
            this.#populateColumns()

            // add listeners for open and close buttons
            this.#initMenuToggleListeners()

            // Add media query listener for isMobile changes
            this._mobileMediaQueryListener = (e) => {
                this._isMobile = e.matches
                this.#populateColumns()
            }
            this._mobileMediaQuery = window.matchMedia('(max-width: 768px)')
            this._mobileMediaQuery.addEventListener('change', this._mobileMediaQueryListener)
        }

        /*
         * Clean up event listeners
         **/
        disconnectedCallback() {
            super.disconnectedCallback()
            // Remove event listeners
            this._menuOpenHandlers.forEach(({ button, handler }) => {
                button.removeEventListener('click', handler)
            })
            this._menuCloseHandlers.forEach(({ button, handler }) => {
                button.removeEventListener('click', handler)
            })
            this.removeEventListener('hedelius-column-click', this.#handleColumnClick)
            this._mobileMediaQuery.removeEventListener('change', this._mobileMediaQueryListener)
        }

        /**
         * Process the existing <ul> that's added to the webcomponent before connect
         */
        #processUl() {
            const rootUl = this.querySelector('ul#nav-tree')
            if (rootUl) {
                this._rootNode = this.#parseMenuItems(rootUl)

                if (this._rootNode.children && this._rootNode.children.length > 0) {
                    this.activeNode = this._rootNode.children[0]
                } else {
                    this.activeNode = this._rootNode
                }
            }
        }

        /**
         * Create NavNode tree from the <ul> element. Expects following structure:
         * <ul>
         *     <li data-entry-type>
         *         <a data-button-title>primary link</a>
         *         <a>secondary link</a>
         *         <p data-show-teaser>teaser text</p>
         *         <img data-teaser-image />
         *         <div data-datasheet><ul><li>...</li></ul></div>
         *     </li>
         * </ul>
         */
        #parseMenuItems(ulElement, parentNode = null) {
            if (!parentNode) {
                parentNode = new NavNode({ parent: null, title: 'root', href: '#' })
            }

            const liElements = ulElement.querySelectorAll(':scope > li[data-entry-type]')
            for (const li of liElements) {
                let nodeProps
                const primaryLink = li.querySelector(':scope > a:first-of-type')
                nodeProps = {
                    href: primaryLink.getAttribute('href'),
                    entry_type: li.dataset.entryType,
                    button_title: primaryLink.dataset.buttonTitle || '',
                    wide_text: primaryLink.children[0]?.dataset.wideText || '',
                    title: primaryLink.children[0].textContent.trim(),
                }

                const secondaryLink = li.querySelector(':scope > a:nth-of-type(2)')
                if (secondaryLink) {
                    nodeProps = {
                        ...nodeProps,
                        second_button_title: secondaryLink.children[0].textContent.trim(),
                        second_button_href: secondaryLink.getAttribute('href') || '',
                    }
                }

                const subtitleSpan = primaryLink.querySelector('span[data-short-subtitle]')
                if (subtitleSpan) {
                    nodeProps = {
                        ...nodeProps,
                        subtitle: subtitleSpan.textContent.trim(),
                        show_subtitle_in_link: subtitleSpan.dataset.showSubtitleInLink || false,
                        short_subtitle: subtitleSpan.dataset.shortSubtitle || '',
                    }
                }

                const teaserDiv = li.querySelector(':scope > div[data-show-teaser]')
                if (teaserDiv) {
                    nodeProps = {
                        ...nodeProps,
                        show_teaser: teaserDiv.dataset.showTeaser,
                        teaser_text: teaserDiv.innerHTML,
                    }
                }

                const image = li.querySelector(':scope > img')
                if (image) {
                    nodeProps = {
                        ...nodeProps,
                        teaser_image: image.dataset.teaserImage,
                        image_alt: image.getAttribute('alt'),
                        image_title: image.getAttribute('title'),
                    }
                }

                const datasheetDiv = li.querySelector('div[data-datasheet]')
                if (datasheetDiv) {
                    const datasheet = []
                    Array.from(datasheetDiv.children).forEach((datapoint) => {
                        datasheet.push({
                            title: datapoint.children[0].textContent.trim(),
                            value: datapoint.children[1].textContent.trim(),
                        })
                    })
                    nodeProps = { ...nodeProps, datasheet: datasheet }
                }

                // Create a new NavNode with all collected properties
                const node = parentNode.addNode(nodeProps)

                // Process nested ul if exists
                const nestedUl = li.querySelector(':scope > ul')
                if (nestedUl) {
                    this.#parseMenuItems(nestedUl, node)
                }
            }
            return parentNode
        }

        /**
         * Transition between two menu columns with a sliding animation.
         */
        #transitionMenuColumn(oldCol, newCol, direction = 'down') {
            oldCol.parentNode.insertBefore(newCol, oldCol)

            newCol.scrollTop = 0

            requestAnimationFrame(() => {
                if (direction === 'up') {
                    oldCol.classList.add('slide-in', 'up')
                    newCol.classList.add('slide-out', 'up')
                } else {
                    oldCol.classList.add('slide-out')
                    newCol.classList.add('slide-in')
                }
                newCol.addEventListener(
                    'animationend',
                    () => {
                        oldCol.remove()
                        newCol.classList.remove('slide-in', 'slide-out', 'up', 'down')
                    },
                    { once: true }
                )
            })
        }

        /**
         * Handle clicks on the menu columns.
         * This method updates the active node and transitions the columns accordingly.
         */
        #handleColumnClick(e) {
            const clickedItem = e.detail.item
            if (!clickedItem || clickedItem.children.length === 0) return

            const direction = this.activeNode.parent === clickedItem ? 'up' : 'down'

            // Animate left column if left column changes
            const leftColumn = this.shadowRoot.querySelector('hedelius-left-column')
            if (leftColumn) {
                if (this._isMobile) {
                    const newRightColumn = document.createElement('hedelius-right-column')
                    newRightColumn.activeNode = clickedItem

                    this.#transitionMenuColumn(leftColumn, newRightColumn, direction)
                } else if (leftColumn.activeNode.parent !== clickedItem.parent) {
                    const newLeftColumn = document.createElement('hedelius-left-column')
                    newLeftColumn.activeNode = clickedItem

                    this.#transitionMenuColumn(leftColumn, newLeftColumn, direction)
                } else {
                    leftColumn.activeNode = clickedItem
                }
            }

            // Handle right column transition
            const rightColumn = this.shadowRoot.querySelector('hedelius-right-column')
            if (rightColumn && this._isMobile && clickedItem === this._rootNode) {
                const newLeftColumn = document.createElement('hedelius-left-column')
                newLeftColumn.activeNode = this._rootNode.children[0]

                this.#transitionMenuColumn(rightColumn, newLeftColumn, direction)
            } else if (rightColumn && rightColumn.activeNode !== clickedItem) {
                const newRightColumn = document.createElement('hedelius-right-column')
                newRightColumn.activeNode = clickedItem

                this.#transitionMenuColumn(rightColumn, newRightColumn, direction)
            }

            this.activeNode = clickedItem
        }

        /**
         * Initialize menu toggle listeners for opening and closing the menu.
         */
        #initMenuToggleListeners() {
            const body = document.querySelector('body')
            const menuBg = this.shadowRoot.querySelector('.menu-bg')
            const menu = this.shadowRoot.querySelector('.wrapper')

            document.querySelectorAll('button[data-menu-button]').forEach((button) => {
                const openHandler = () => {
                    body.classList.add('no-scroll')
                    menu.classList.add('open')
                    menuBg.classList.add('open')
                    document.dispatchEvent(new Event('menu-click'))
                }
                button.addEventListener('click', openHandler)
                this._menuOpenHandlers.push({ button, handler: openHandler })
            })

            const mobileHeader = this.shadowRoot.querySelector('hedelius-mobile-header')
            mobileHeader.updateComplete.then(() => {
                const closeButtons = [
                    ...mobileHeader.shadowRoot.querySelectorAll('button[data-menu-close-button]'),
                    ...this.shadowRoot.querySelectorAll('button[data-menu-close-button]'),
                ]
                closeButtons.forEach((button) => {
                    const closeHandler = () => {
                        body.classList.remove('no-scroll')
                        menu.classList.remove('open')
                        menuBg.classList.remove('open')
                    }
                    button.addEventListener('click', closeHandler)
                    this._menuCloseHandlers.push({ button, handler: closeHandler })
                })
            })
        }

        /**
         * Creates and populates the left and right columns based on the current active node and isMobile state.
         */
        #populateColumns() {
            // Remove old columns
            const leftContainer = this.shadowRoot.querySelector('.left-column')
            const rightContainer = this.shadowRoot.querySelector('.right-column')
            const currentColumns = [
                ...leftContainer.querySelectorAll('hedelius-left-column'),
                ...rightContainer.querySelectorAll('hedelius-left-column, hedelius-right-column'),
            ]
            if (currentColumns) {
                currentColumns.forEach((column) => {
                    column.remove()
                })
            }

            // Reset left and right columns based on isMobile state
            let rightColumn
            let leftColumn
            if (this._isMobile && this.activeNode.parent === this._rootNode) {
                rightColumn = document.createElement('hedelius-left-column')
            } else {
                rightColumn = document.createElement('hedelius-right-column')
                this._isMobile
                    ? (leftColumn = null)
                    : (leftColumn = document.createElement('hedelius-left-column'))
            }

            rightColumn.activeNode = this.activeNode
            this.shadowRoot.querySelector('.right-column').appendChild(rightColumn)

            if (leftColumn) {
                leftColumn.activeNode = this.activeNode
                this.shadowRoot.querySelector('.left-column').appendChild(leftColumn)
                this.shadowRoot.querySelector('.left-column').scrollTop = 0
            }
        }

        _closeButton() {
            return html`
                <button
                    data-menu-close-button
                    type="button"
                    class="m-5 h-12 w-12 text-xl text-white"
                >
                    <svg
                        fill="none"
                        stroke="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 29.8 29.8"
                    >
                        <g id="nav_kreuz" data-name="nav kreuz">
                            <g>
                                <line
                                    style="stroke-width: 2px;"
                                    x1="9.85"
                                    y1="9.85"
                                    x2="19.95"
                                    y2="19.95"
                                />
                                <line
                                    style="stroke-width: 2px;"
                                    x1="19.95"
                                    y1="9.85"
                                    x2="9.85"
                                    y2="19.95"
                                />
                            </g>
                            <circle cx="14.9" cy="14.9" r="14.4" />
                        </g>
                    </svg>
                </button>
            `
        }

        /**
         * #populateColumns inserts the left and right columns into the menu, depending on mobile or desktop view.
         */
        render() {
            return html`
                <link rel="stylesheet" href="${styleUrl}" />
                <div class="wrapper">
                    <div class="empty-column flex flex-col items-end justify-center max-md:hidden">
                        ${this._closeButton()}
                    </div>
                    <div class="left-column relative z-[100] bg-white">
                        <hedelius-scroll-indicator
                            .activeNode="${this.activeNode}"
                            side="left"
                        ></hedelius-scroll-indicator>
                    </div>
                    <div class="right-column relative h-full max-h-full bg-white">
                        <hedelius-mobile-header></hedelius-mobile-header>
                        <hedelius-scroll-indicator
                            .activeNode="${this.activeNode}"
                            side="right"
                        ></hedelius-scroll-indicator>
                    </div>
                </div>
                <div class="menu-bg max-md:hidden"></div>
            `
        }
    }
)
